// src/utils/readAnnotationsFromDicom.js
import dcmjs from "dcmjs";

/**
 * Custom error classes for annotation reading
 */
export class AnnotationReadError extends Error {
  constructor(message, code, details = {}) {
    super(message);
    this.name = 'AnnotationReadError';
    this.code = code;
    this.details = details;
  }
}

/**
 * Reads annotations from a DICOM file's private tags
 * @param {Object} dicomMessage - The parsed DICOM message
 * @param {Object} imageMetadata - Image dimensions for coordinate validation
 * @returns {Object} - Annotations grouped by frame index
 */
export function readAnnotationsFromDicom(dicomMessage, imageMetadata = null) {
  try {
    if (!dicomMessage || !dicomMessage.dict) {
      console.log("No DICOM message or dict provided for annotation reading");
      return {};
    }

    const dict = dicomMessage.dict;
    
    // Look for our private annotation tags
    // We use group 0x0029 with private creator "MY_ANNOTATIONS"
    const privateGroup = 0x0029;
    const groupHex = privateGroup.toString(16).padStart(4, "0");
    const creatorElemHex = (0x0010).toString(16).padStart(4, "0");
    const privateElemHex = (0x1001).toString(16).padStart(4, "0");
    
    // Try different key formats that might be used
    const possibleCreatorKeys = [
      `x${groupHex}${creatorElemHex}`,           // x00290010
      `${groupHex}${creatorElemHex}`,            // 00290010
      `x${groupHex.toUpperCase()}${creatorElemHex.toUpperCase()}`, // x00290010 (uppercase)
      `${groupHex.toUpperCase()}${creatorElemHex.toUpperCase()}`   // 00290010 (uppercase)
    ];
    
    const possiblePrivateKeys = [
      `x${groupHex}${privateElemHex}`,           // x00291001
      `${groupHex}${privateElemHex}`,            // 00291001
      `x${groupHex.toUpperCase()}${privateElemHex.toUpperCase()}`, // x00291001 (uppercase)
      `${groupHex.toUpperCase()}${privateElemHex.toUpperCase()}`   // 00291001 (uppercase)
    ];

    console.log("Looking for annotation keys with possible formats:");
    console.log("Creator keys:", possibleCreatorKeys);
    console.log("Private keys:", possiblePrivateKeys);
    console.log("Available dict keys containing 0029:", Object.keys(dict).filter(k => k.toLowerCase().includes('0029')));
    console.log("All dict keys (first 50):", Object.keys(dict).slice(0, 50));
    
    // Let's also check what the actual values are for any 0029 keys
    const group0029Keys = Object.keys(dict).filter(k => k.toLowerCase().includes('0029'));
    group0029Keys.forEach(key => {
      console.log(`Key ${key}:`, dict[key]);
    });

    // Find the actual creator key
    let creatorKey = null;
    let creatorElement = null;
    
    for (const key of possibleCreatorKeys) {
      if (dict[key] && dict[key].Value && dict[key].Value[0] === "MY_ANNOTATIONS") {
        creatorKey = key;
        creatorElement = dict[key];
        break;
      }
    }
    
    if (!creatorElement) {
      console.log("Private creator not found with any key format. Checked keys:", possibleCreatorKeys);
      // Let's also check if there are any elements with our creator value
      const allCreatorElements = Object.entries(dict).filter(([key, value]) => 
        value && value.Value && value.Value[0] === "MY_ANNOTATIONS"
      );
      console.log("Elements with MY_ANNOTATIONS value:", allCreatorElements);
      if (allCreatorElements.length > 0) {
        console.log("Found MY_ANNOTATIONS elements with different keys:");
        allCreatorElements.forEach(([key, value]) => {
          console.log(`  Key: ${key}, Value:`, value);
        });
        
        // If we found the creator with a different key, let's look for the annotation data nearby
        const creatorKey = allCreatorElements[0][0];
        console.log("Using creator key:", creatorKey);
        
        // Try to find annotation data with similar key patterns
        const creatorKeyBase = creatorKey.replace(/10$/, ''); // Remove the '10' part
        const possibleDataKeys = [
          creatorKeyBase + '01',
          creatorKeyBase + '1001',
          creatorKey.replace('10', '01'),
          creatorKey.replace('10', '1001'),
          creatorKey.replace('0010', '1001')
        ];
        
        console.log("Looking for annotation data with keys:", possibleDataKeys);
        
        for (const dataKey of possibleDataKeys) {
          if (dict[dataKey] && dict[dataKey].Value && dict[dataKey].Value[0]) {
            console.log(`Found annotation data with key ${dataKey}:`, dict[dataKey]);
            
            // Try to parse and use this data
            try {
              const annotationsJson = dict[dataKey].Value[0];
              const annotationData = JSON.parse(annotationsJson);
              
              if (annotationData && annotationData.annotations) {
                console.log("Successfully found and parsed annotation data!");
                
                // Convert and return the annotations
                const annotations = annotationData.annotations || {};
                const convertedAnnotations = {};
                
                for (const [frameKey, frameAnnotations] of Object.entries(annotations)) {
                  if (!Array.isArray(frameAnnotations)) continue;
                  
                  const convertedFrameAnnotations = [];
                  for (const annotation of frameAnnotations) {
                    try {
                      const convertedAnnotation = convertAnnotationFromStorage(annotation, imageMetadata);
                      convertedFrameAnnotations.push(convertedAnnotation);
                    } catch (error) {
                      console.warn(`Failed to convert annotation in frame ${frameKey}:`, annotation, error.message);
                    }
                  }
                  
                  if (convertedFrameAnnotations.length > 0) {
                    convertedAnnotations[frameKey] = convertedFrameAnnotations;
                  }
                }
                
                console.log("Converted annotations for viewer:", convertedAnnotations);
                return convertedAnnotations;
              }
            } catch (e) {
              console.warn(`Failed to parse annotation data from key ${dataKey}:`, e);
            }
          }
        }
      }
      return {};
    }

    console.log("Found creator element with key:", creatorKey);

    // Find the actual private key
    let privateKey = null;
    let annotationElement = null;
    
    for (const key of possiblePrivateKeys) {
      if (dict[key] && dict[key].Value && dict[key].Value[0]) {
        privateKey = key;
        annotationElement = dict[key];
        break;
      }
    }
    
    if (!annotationElement) {
      console.log("Annotation data element not found with any key format. Checked keys:", possiblePrivateKeys);
      return {};
    }

    console.log("Found annotation element with key:", privateKey);

    const annotationsJson = annotationElement.Value[0];
    console.log("Found annotation JSON:", annotationsJson.substring(0, 200) + "...");

    // Parse the JSON
    let annotationData;
    try {
      annotationData = JSON.parse(annotationsJson);
    } catch (parseError) {
      throw new AnnotationReadError(
        "Failed to parse annotation JSON data",
        "JSON_PARSE_ERROR",
        { originalError: parseError.message, jsonData: annotationsJson.substring(0, 500) }
      );
    }

    // Validate the annotation data structure
    if (!annotationData || typeof annotationData !== 'object') {
      throw new AnnotationReadError(
        "Invalid annotation data structure",
        "INVALID_STRUCTURE",
        { data: annotationData }
      );
    }

    console.log("Parsed annotation data:", annotationData);

    // Extract annotations from the data
    const annotations = annotationData.annotations || {};
    
    // Convert back to the format expected by the viewer
    const convertedAnnotations = {};
    
    for (const [frameKey, frameAnnotations] of Object.entries(annotations)) {
      if (!Array.isArray(frameAnnotations)) {
        console.warn(`Invalid frame annotations for frame ${frameKey}:`, frameAnnotations);
        continue;
      }

      const convertedFrameAnnotations = [];
      
      for (const annotation of frameAnnotations) {
        try {
          const convertedAnnotation = convertAnnotationFromStorage(annotation, imageMetadata);
          convertedFrameAnnotations.push(convertedAnnotation);
        } catch (error) {
          console.warn(`Failed to convert annotation in frame ${frameKey}:`, annotation, error.message);
        }
      }

      if (convertedFrameAnnotations.length > 0) {
        convertedAnnotations[frameKey] = convertedFrameAnnotations;
      }
    }

    console.log("Converted annotations for viewer:", convertedAnnotations);
    
    return convertedAnnotations;

  } catch (error) {
    if (error instanceof AnnotationReadError) {
      console.error("Annotation read error:", error);
      throw error;
    }
    
    console.error("Unexpected error reading annotations:", error);
    throw new AnnotationReadError(
      "Unexpected error reading annotations from DICOM",
      "UNEXPECTED_ERROR",
      { originalError: error.message }
    );
  }
}

/**
 * Converts an annotation from storage format back to viewer format
 * @param {Object} storedAnnotation - Annotation as stored in DICOM
 * @param {Object} imageMetadata - Image dimensions for coordinate conversion
 * @returns {Object} - Annotation in viewer format
 */
function convertAnnotationFromStorage(storedAnnotation, imageMetadata) {
  if (!storedAnnotation || typeof storedAnnotation !== 'object') {
    throw new Error("Invalid stored annotation format");
  }

  // Check if this is a geometric annotation (line, rectangle, sketch) or a tag annotation
  if (storedAnnotation.type && ['line', 'rectangle', 'sketch'].includes(storedAnnotation.type)) {
    // This is a geometric annotation - return it as-is with proper structure
    const { type, color, id } = storedAnnotation;
    
    if (type === 'line' || type === 'rectangle') {
      const { startX, startY, endX, endY } = storedAnnotation;
      return {
        type,
        startX: Math.max(0, Math.min(1, startX)),
        startY: Math.max(0, Math.min(1, startY)),
        endX: Math.max(0, Math.min(1, endX)),
        endY: Math.max(0, Math.min(1, endY)),
        color: color || '#22D3EE',
        id: id || Date.now()
      };
    } else if (type === 'sketch') {
      const { points } = storedAnnotation;
      if (!Array.isArray(points)) {
        throw new Error("Invalid sketch points format");
      }
      return {
        type,
        points: points.map(point => ({
          x: Math.max(0, Math.min(1, point.x)),
          y: Math.max(0, Math.min(1, point.y))
        })),
        color: color || '#22D3EE',
        id: id || Date.now()
      };
    }
  }

  // This is a tag annotation - handle the legacy format
  const { x, y, normalizedX, normalizedY, label } = storedAnnotation;

  if (typeof x !== 'number' || typeof y !== 'number') {
    throw new Error("Missing or invalid pixel coordinates in stored annotation");
  }

  if (!label || typeof label !== 'string') {
    throw new Error("Missing or invalid label in stored annotation");
  }

  // Convert back to the format expected by the viewer (ix, iy for normalized coordinates)
  let ix, iy;

  if (typeof normalizedX === 'number' && typeof normalizedY === 'number') {
    // Use stored normalized coordinates if available
    ix = normalizedX;
    iy = normalizedY;
  } else if (imageMetadata && imageMetadata.cols > 0 && imageMetadata.rows > 0) {
    // Calculate normalized coordinates from pixel coordinates
    ix = x / (imageMetadata.cols - 1);
    iy = y / (imageMetadata.rows - 1);
    console.log(`Converting pixel (${x}, ${y}) to normalized (${ix.toFixed(3)}, ${iy.toFixed(3)}) using dimensions ${imageMetadata.cols}x${imageMetadata.rows}`);
  } else {
    // Fallback: assume normalized coordinates are the same as pixel coordinates (not ideal)
    console.warn("No valid image metadata available for coordinate conversion, using fallback method");
    console.warn("Image metadata:", imageMetadata);
    // Use the stored normalized coordinates if available, otherwise estimate
    if (typeof normalizedX === 'number' && typeof normalizedY === 'number') {
      ix = normalizedX;
      iy = normalizedY;
      console.log(`Using stored normalized coordinates: (${ix}, ${iy})`);
    } else {
      // Last resort: rough approximation
      ix = x / 1000;
      iy = y / 1000;
      console.warn(`Using rough approximation: pixel (${x}, ${y}) -> normalized (${ix}, ${iy})`);
    }
  }

  return {
    ix: Math.max(0, Math.min(1, ix)), // Clamp to [0, 1]
    iy: Math.max(0, Math.min(1, iy)), // Clamp to [0, 1]
    label: label.trim()
  };
}

/**
 * Attempts to read annotations from a DICOM file with error handling
 * @param {Object} dicomMessage - The parsed DICOM message
 * @param {Object} imageMetadata - Image dimensions
 * @returns {Object} - { success: boolean, annotations: Object, error?: string }
 */
export function tryReadAnnotationsFromDicom(dicomMessage, imageMetadata = null) {
  try {
    const annotations = readAnnotationsFromDicom(dicomMessage, imageMetadata);
    const annotationCount = Object.values(annotations).reduce((sum, frameAnnotations) => sum + frameAnnotations.length, 0);
    
    return {
      success: true,
      annotations,
      stats: {
        framesWithAnnotations: Object.keys(annotations).length,
        totalAnnotations: annotationCount
      }
    };
  } catch (error) {
    console.warn("Failed to read annotations from DICOM:", error.message);
    return {
      success: false,
      annotations: {},
      error: error.message,
      stats: {
        framesWithAnnotations: 0,
        totalAnnotations: 0
      }
    };
  }
}